import { type ReactNode } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../shadcn/dropdown-menu';
import { Button } from './Button';
import type { TColors, TSizes } from '../../types';

interface IButtonDropdownItem {
  value: string;
  label: string;
  secondaryLabel?: string;
  onClick?: (value: string) => void;
  disabled?: boolean;
}

interface IButtonDropdownProps {
  items: readonly IButtonDropdownItem[];
  value?: string;
  onChange?: (value: string) => void;
  variant?: 'filled' | 'outline' | 'ghost';
  color?: TColors;
  size?: TSizes;
  label?: string;
  disabled?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  className?: string;
  contentClassName?: string;
  id?: string;
}

export function ButtonDropdown(props: IButtonDropdownProps) {
  const {
    items,
    value,
    onChange,
    variant = 'filled',
    color = 'primary',
    size = 'm',
    label,
    disabled,
    iconLeft,
    iconRight,
    className,
    contentClassName,
    id,
    ...rest
  } = props;

  const currentLabel =
    label ?? items.find((i) => i.value === value)?.label ?? '';

  const handleSelect = (selectedValue: string) => {
    onChange?.(selectedValue);
    const item = items.find((i) => i.value === selectedValue);
    item?.onClick?.(selectedValue);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          color={color}
          size={size}
          disabled={disabled}
          iconLeft={iconLeft}
          iconRight={iconRight}
          className={className}
          id={id}
          {...rest}
        >
          {currentLabel}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className={contentClassName}>
        {items.map((item) => (
          <DropdownMenuItem
            key={item.value}
            aria-label={item.secondaryLabel ? item.label : undefined}
            onSelect={() => handleSelect(item.value)}
            disabled={item.disabled}
          >
            {item.secondaryLabel ? (
              <span className="flex w-full items-center justify-between gap-3">
                <span>{item.label}</span>
                <span className="shrink-0 text-xs font-normal text-muted-foreground">
                  {item.secondaryLabel}
                </span>
              </span>
            ) : (
              item.label
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
